import { Observable, throwError } from 'rxjs';
import {
  DocumentFileTypeDTO, DownloadableDTO, FileDetailsDTO,
  SignatureDTO, SignatureType, SignatureUpgradeDTO, TmpFileDTO
} from '../../../app/api/plyca-dtos';
import { BaseDirectiveObject } from '../../../app/components/common/base-directive-object';
import { PlycaDataService } from '../../plyca.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  CertificateSelectionResponse, SignatureProviderService,
  SignatureProviderType,
  SignatureRequest, SignatureResponse, SignatureStatus
} from '../signature.objects';
import { SignatureUtils } from '../../signature.utils';


const EXTENSION_P7 = 'p7s';
const EXTENSION_PADES = 'pdf';
const EXTENSION_XADES = 'xsig';
const EXTENSION_OTHER = 'sig';

/**
 * Clase base de las implementaciones de firma
 */
export abstract class BaseSignatureProvider extends BaseDirectiveObject implements SignatureProviderService {

  constructor(protected plycaService: PlycaDataService, protected translateService: TranslateService,
    protected signatureUtils: SignatureUtils) {

    super();
  }

  isEnabled(): boolean {
    return true;
  }

  /**
   * Metodo de firma
   * @param request
   */
  public abstract sign(request: SignatureRequest): Observable<SignatureResponse>;
  /**
   * Metodo de seleccion de certificado de firma
   */
  public abstract selectCertificate(): Observable<CertificateSelectionResponse>;
  /**
   * Operacion de comprobacion de la conexion
   */
  public abstract check(): Observable<SignatureStatus>;

  /**
 * Tipo de proveedor de firma.
 * Utilizado para leer la configuracion
 *
 * @return valor enumerado asociado SignatureProviderType
 */
  protected abstract getSignatureProviderType(): SignatureProviderType;

  // metodos de utilidad
  /**
   * Metodo que devuelve el contenido a firmar en B64
   * @param file
   * @returns
   */
  protected getContentToSign(file: FileDetailsDTO): Observable<string> {
    let b64ToSign: Observable<string>;
    if (
      file.signatures &&
      file.signatures.length > 0 &&
      (file.signatures[0].signatureFormat.mode === 'Detached' && !file.signatures[0].signatureFormat.oneSignaturePerFile)
    ) {
      let signature: SignatureDTO = file.signatures[0];
      while (
        signature.signatures &&
        signature.signatures.length > 0 &&
        signature.signatures[0].download
      ) {
        signature = signature.signatures[0];
      }
      b64ToSign = this.plycaService.getDownloadableContentB64(
        signature.download
      );
    } else {
      if (file.fileType === DocumentFileTypeDTO.Temporal) {
        b64ToSign = this.plycaService.getTmpFileContentB64(file);
      } else {
        b64ToSign = this.plycaService.getFileContentB64(file);
      }
    }
    return b64ToSign.pipe(
      catchError((error: any) => {
        return this.manageSignatureError(error, 'Signature.AutoFirma.Error.Upload');
      }),
    );
  }

  protected manageSignatureError(error: any, message: string) {
    console.error(error);
    return throwError(new Error(this.translateService.instant(message)));
  }

  /**
   *
   * @param signatureRequest Metodo de subida de la firma a servidor
   * @param b64Signature
   * @returns datos de la firma subida al servidor y validacion asociada
   */
  protected uploadAndProcessSignature(signatureRequest: SignatureRequest,
    b64Signature: string): Observable<SignatureResponse> {

    let response = new SignatureResponse();
    response.request = signatureRequest;
    console.log('Firma realizada correctamente, subiendo firma al temporal');
    let tmpSignFile: TmpFileDTO = {} as TmpFileDTO;
    const file = signatureRequest.file;
    tmpSignFile.b64Content = b64Signature;
    tmpSignFile.target = file.identifier;
    tmpSignFile.comment = signatureRequest.comment;
    tmpSignFile.desiredSignatureUpgrade = this.getSignatureUpgrade(signatureRequest);
    tmpSignFile.name = this.getSignatureFileName(file, signatureRequest.signatureFormat.type);
    return this.plycaService.uploadSignature(tmpSignFile).pipe(
      catchError((error: any) => {
        return this.manageSignatureError(error, 'Upload.error.signature');
      }),
      switchMap((uploadedFile: DownloadableDTO) => {
        console.log('Firma subida correctamente, consultando detalles del fichero original', uploadedFile);
        return this.plycaService.fileDetailsDocumentWithSignatures(uploadedFile.fileDetails.links[0].href)
      }),
      catchError((error: any) => {
        return this.manageSignatureError(error, 'Upload.details.error');
      }),
      map((fileDetails: FileDetailsDTO) => {
        response.status = SignatureStatus.OK;
        response.file = fileDetails;
        const errSignature = this.signatureUtils.getFirstSignatureWithVerificationEror(fileDetails.signatures)
        if (errSignature) {
          response.errorMessage = (errSignature?.verification?.validationMessages?.length > 0) ?
            errSignature.verification.validationMessages[0] : undefined;
          response.signatureStatus = this.signatureUtils.getValidOrEmptySignatureCode(errSignature);
        } else {
          const lastSignature = this.signatureUtils.getLastSignature(fileDetails.signatures);
          response.signatureStatus = this.signatureUtils.getValidOrEmptySignatureCode(lastSignature);
        }
        return response;
      })
    );
  }

  private getSignatureFileName(
    signedFile: FileDetailsDTO,
    signatureFormat: SignatureType
  ): string {
    let fileName: string = signedFile.name;
    let fileExtension: string;
    let signatureName: string = fileName;
    let signatureExtension: string = this.getSignatureExtension(
      signatureFormat
    );
    let extensionIndex: number = fileName.lastIndexOf('.');
    if (extensionIndex > 0) {
      fileExtension = fileName.substring(extensionIndex);
    }
    if (fileExtension !== signatureExtension) {
      signatureName += '.' + signatureExtension;
    }
    console.log(signatureName + ' created');
    return signatureName;
  }

  private getSignatureExtension(format: SignatureType): string {
    let extension: string;
    switch (format) {
      case SignatureType.PKCS7:
      case SignatureType.CAdES: {
        extension = EXTENSION_P7;
        break;
      }
      case SignatureType.XAdES: {
        extension = EXTENSION_XADES;
        break;
      }
      case SignatureType.PAdES: {
        extension = EXTENSION_PADES;
        break;
      }
      default: {
        extension = EXTENSION_OTHER;
        break;
      }
    }
    return extension;
  }

  private getSignatureUpgrade(signatureRequest: SignatureRequest): SignatureUpgradeDTO {
    let documentClassConfiguration = signatureRequest.documentClass?.configuration?.signatureUpgradeConfiguration;
    let defaultConfig: SignatureUpgradeDTO = undefined;
    if (documentClassConfiguration) {
      for (let config of documentClassConfiguration) {
        if (!config.appliesTo) {
          defaultConfig = config;
        } else if (config.appliesTo.type === signatureRequest.signatureFormat.type
          && config.appliesTo.mode === signatureRequest.signatureFormat.mode) {
          return config;
        }
      }
    }
    return defaultConfig;
  }

}
